// Maps each form-field component in InsuranceFormFields.tsx to the
// PolicyFundingSource fields it edits. The Policy Details card uses this
// to render exactly the inputs the form would show for a given policy
// type — see usage in `buildPolicyDetails.ts`.

import { type FC } from 'react';
import { FORM_FIELD_COMPONENTS } from '@/components/InsuranceFormFields';
import { FormatKind } from './types';

export interface PolicyFieldDescriptor {
  label: string;
  key: keyof PolicyFundingSource;
  format: FormatKind;
  tooltip?: string;
}

export type FormFieldGroup =
  | { kind: 'fields'; fields: PolicyFieldDescriptor[] }
  | { kind: 'perEnv' };

const C = FORM_FIELD_COMPONENTS;

// One form component may render multiple FieldContainers; the descriptor
// is a flat list of fields with no per-component title (the card renders
// a single flat grid).
const componentDescriptors = new Map<FC, FormFieldGroup[]>([
  [
    C.WaitingPeriodDaysFieldGroup,
    [
      {
        kind: 'fields',
        fields: [
          { label: 'Home care WP', key: 'homeCareWaitingPeriodDays', format: 'days' },
          { label: 'Facility care WP', key: 'facilityCareWaitingPeriodDays', format: 'days' },
          {
            label: 'Retro WP reimbursement',
            key: 'hasRetroactiveWaitingPeriodReimbursement',
            format: 'boolean',
          },
        ],
      },
    ],
  ],
  [
    C.FaceAmountField,
    [
      {
        kind: 'fields',
        fields: [{ label: 'Face amount', key: 'policyMaximumBenefitAmount', format: 'currency' }],
      },
    ],
  ],
  [
    C.FaceAmountAtAgeField,
    [
      {
        kind: 'fields',
        fields: [
          { label: 'Face amount at LTC start', key: 'policyMaximumBenefitAmount', format: 'currency' },
        ],
      },
    ],
  ],
  [
    C.MaximumBenefitAmountField,
    [
      {
        kind: 'fields',
        fields: [{ label: 'Max benefit', key: 'policyMaximumBenefitAmount', format: 'currency' }],
      },
    ],
  ],
  [
    C.TotalLTCBalanceAtAgeField,
    [
      {
        kind: 'fields',
        fields: [
          { label: 'Total LTC balance at age', key: 'policyMaximumBenefitAmount', format: 'currency' },
        ],
      },
    ],
  ],
  [
    C.JointTotalLTCBalanceAtAgeField,
    [
      {
        kind: 'fields',
        fields: [
          { label: 'Total LTC balance at age', key: 'policyMaximumBenefitAmount', format: 'currency' },
        ],
      },
    ],
  ],
  [
    C.JointDeathBenefitAtAgeField,
    [
      {
        kind: 'fields',
        fields: [
          { label: 'Face amount at age', key: 'policyMaximumBenefitAmount', format: 'currency' },
        ],
      },
    ],
  ],
  [
    C.MinimumGuaranteedBenefitAmountField,
    [
      {
        kind: 'fields',
        fields: [
          {
            label: 'Min guaranteed DB',
            key: 'policyMinimumGuaranteedBenefitAmount',
            format: 'currency',
          },
        ],
      },
    ],
  ],
  [
    C.ContinuationOfBenefitsField,
    [
      {
        kind: 'fields',
        fields: [
          { label: 'COB amount', key: 'policyContinuationBenefitAmount', format: 'currency' },
        ],
      },
    ],
  ],
  [
    C.AccelerationBenefitDurationMonthsField,
    [
      {
        kind: 'fields',
        fields: [
          {
            label: 'Acceleration duration',
            key: 'policyAccelerationBenefitDurationMonths',
            format: 'months',
          },
        ],
      },
    ],
  ],
  [
    C.EobBenefitDurationMonthsField,
    [
      {
        kind: 'fields',
        fields: [
          {
            label: 'EOB duration',
            key: 'policyEobBenefitDurationMonths',
            format: 'months',
          },
        ],
      },
    ],
  ],
  [
    C.BenefitPeriodMonthsField,
    [
      {
        kind: 'fields',
        fields: [
          { label: 'Benefit period', key: 'policyBenefitPeriodMonths', format: 'months' },
        ],
      },
    ],
  ],
  [
    C.PremiumStartYearField,
    [
      {
        kind: 'fields',
        fields: [
          { label: 'Premium start year', key: 'policyPremiumStartYear', format: 'year' },
        ],
      },
    ],
  ],
  [
    C.PremiumMonthlyCostField,
    [
      {
        kind: 'fields',
        fields: [
          { label: 'Monthly premium', key: 'policyPremiumMonthlyCost', format: 'currency' },
        ],
      },
    ],
  ],
  [
    C.PremiumIncreaseRateField,
    [
      {
        kind: 'fields',
        fields: [
          { label: 'Premium increase rate', key: 'policyPremiumIncreaseRate', format: 'percent' },
        ],
      },
    ],
  ],
  [
    C.PremiumIncreaseYearsField,
    [
      {
        kind: 'fields',
        fields: [
          { label: 'Premium increase years', key: 'policyPremiumIncreaseYears', format: 'years' },
        ],
      },
    ],
  ],
  [
    C.LimitedPayYearsField,
    [
      {
        kind: 'fields',
        fields: [{ label: 'Limited-pay years', key: 'policyLimitedPayYears', format: 'years' }],
      },
    ],
  ],
  [
    C.LumpSumPaymentField,
    [
      {
        kind: 'fields',
        fields: [{ label: 'Lump-sum payment', key: 'policyLumpSumPayment', format: 'currency' }],
      },
    ],
  ],
  [C.DailyMaxBenefitsFieldGroup, [{ kind: 'perEnv' }]],
  [
    C.InflationProtectionField,
    [
      {
        kind: 'fields',
        fields: [
          { label: 'Inflation protection', key: 'policyInflationProtection', format: 'percent' },
        ],
      },
    ],
  ],
  [
    C.InflationProtectionOnFaceAmountField,
    [
      {
        kind: 'fields',
        fields: [
          {
            label: 'Inflation on face',
            key: 'policyInflationProtectionOnFaceAmount',
            format: 'percent',
          },
        ],
      },
    ],
  ],
  [
    C.InflationProtectionOnCOBAmountField,
    [
      {
        kind: 'fields',
        fields: [
          {
            label: 'Inflation on COB',
            key: 'policyInflationProtectionOnCOBAmount',
            format: 'percent',
          },
        ],
      },
    ],
  ],
  [
    C.IsSimpleInflationProtectionField,
    [
      {
        kind: 'fields',
        fields: [
          {
            label: 'Simple inflation',
            tooltip: 'When true, inflation is applied as a flat annual increase rather than compounding.',
            key: 'isSimpleInflationProtection',
            format: 'boolean',
          },
        ],
      },
    ],
  ],
  [
    C.InflationProtectionFieldGroup,
    [
      {
        kind: 'fields',
        fields: [
          { label: 'Inflation protection', key: 'policyInflationProtection', format: 'percent' },
          {
            label: 'Simple inflation',
            key: 'isSimpleInflationProtection',
            format: 'boolean',
          },
        ],
      },
    ],
  ],
  [
    C.InflationProtectionYearsField,
    [
      {
        kind: 'fields',
        fields: [
          {
            label: 'Inflation protection years',
            key: 'policyInflationProtectionYears',
            format: 'years',
          },
        ],
      },
    ],
  ],
  [
    C.IsIndemnityPolicyPaymentField,
    [
      {
        kind: 'fields',
        fields: [
          {
            label: 'Indemnity',
            tooltip:
              'Form input flag. Note: a strategy can override this (e.g. CareMatters II is always indemnity).',
            key: 'isIndemnityPolicyPayment',
            format: 'boolean',
          },
        ],
      },
    ],
  ],
  [
    C.PrimaryPolicyHolderField,
    [
      {
        kind: 'fields',
        fields: [
          { label: 'Is primary holder', key: 'isPrimaryPolicyHolder', format: 'boolean' },
        ],
      },
    ],
  ],
  [
    C.PolicyMonthlyWithdrawalRate,
    [
      {
        kind: 'fields',
        fields: [
          {
            label: 'Monthly withdrawal rate',
            key: 'policyMonthlyWithdrawalRate',
            format: 'percent',
          },
        ],
      },
    ],
  ],
  [
    C.BenefitUtilizedToDateField,
    [
      {
        kind: 'fields',
        fields: [
          {
            label: 'Benefit utilized to date',
            key: 'policyBenefitUtilizedToDate',
            format: 'currency',
          },
        ],
      },
    ],
  ],
]);

export function descriptorsForFormFields(formFields: FC[]): FormFieldGroup[] {
  const result: FormFieldGroup[] = [];
  for (const Fc of formFields) {
    const groups = componentDescriptors.get(Fc);
    if (groups) result.push(...groups);
  }
  return result;
}
